import Big from "big.js";
import { useMemo, useState } from "react";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Combobox, formatCurrency } from "medere-ui-framework";
import { ProfessionalAssignmentDTO } from "../../../dto/budget/ProfessionalAssignmentDTO";
import { getProfessionals } from "../../../servicesHooks/Budget/useBudgetService";
// TODO: estructura de distribución deshabilitada por ahora; se vuelve a habilitar más adelante.
// import { getDistributionStructures } from "../../../servicesHooks/Budget/useBudgetService";
// import DistributionStructureDialog from "../DistributionStructureDialog";
import { formatPercent, getAssignmentsTotal, isFullyDistributed, roundTwo } from "./billItems";
import LoadTable, { LoadTableColumn, LoadTableRow } from "../../../components/novarium/LoadTable";
import NumberField from "./NumberField";
import { AddRowButton, FieldError } from "./tableParts";

/** Profesional del combo: el backend manda también su rol. */
interface ProfessionalComboDTO {
    id: number | null;
    name: string | null;
    role?: number;
    rolename?: string;
}

type Row = LoadTableRow & { assignment: ProfessionalAssignmentDTO; index: number };

interface UnitDistributionSectionProps {
    unitLabel: string;
    assignments: ProfessionalAssignmentDTO[];
    /** Honorario de una unidad: base del importe de cada profesional. */
    medicalAmount: number;
    error?: string | null;
    canApplyToAll: boolean;
    /** Vista previa: la distribución como tabla, sin campos. */
    readOnly?: boolean;
    onChange: (assignments: ProfessionalAssignmentDTO[]) => void;
    onApplyToAll: () => void;
    onClose: () => void;
}

interface AddDraft {
    professional: ProfessionalComboDTO | null;
    text: string;
    percentage: number | null;
    errors: { professional?: string; percentage?: string };
}

const emptyDraft = (): AddDraft => ({ professional: null, text: '', percentage: null, errors: {} });

export default function UnitDistributionSection({ unitLabel, assignments, medicalAmount, error, canApplyToAll, readOnly = false, onChange, onApplyToAll, onClose }: UnitDistributionSectionProps) {
    const [draft, setDraft] = useState<AddDraft>(emptyDraft);
    const hasMedicalAmount = medicalAmount > 0;
    const total = getAssignmentsTotal(assignments);
    const remaining = Math.max(0, roundTwo(100 - total));

    const amountOf = (percentage: number) => hasMedicalAmount ? new Big(medicalAmount).times(percentage).div(100).round(2, Big.roundHalfUp).toNumber() : 0;
    const percentageOf = (amount: number) => hasMedicalAmount ? new Big(amount).div(medicalAmount).times(100).round(2, Big.roundHalfUp).toNumber() : 0;

    const rows: Row[] = useMemo(() => assignments.map((assignment, index) => ({ rowKey: String(assignment.id ?? index), assignment, index })), [assignments]);

    const setPercentage = (index: number, percentage: number) =>
        onChange(assignments.map((a, i) => i === index ? { ...a, percentage: Math.min(100, Math.max(0, percentage)) } : a));

    const pickProfessional = (e: ProfessionalComboDTO | null) => {
        if (!e?.id) {
            setDraft(prev => ({ ...prev, professional: null, text: e?.name ?? '' }));
            return;
        }
        setDraft(prev => ({
            ...prev,
            professional: e,
            text: e.name ?? '',
            percentage: prev.percentage ?? (remaining > 0 ? remaining : null),
            errors: { ...prev.errors, professional: undefined },
        }));
    };

    const add = () => {
        const errors: AddDraft['errors'] = {};
        const professional = draft.professional;
        const percentage = draft.percentage ?? 0;
        if (!professional?.id) errors.professional = 'Elegí un profesional';
        else if (assignments.some(a => a.id === professional.id)) errors.professional = 'Ese profesional ya está asignado';
        if (percentage <= 0 || percentage > 100) errors.percentage = 'Ingresá un porcentaje entre 1 y 100';
        else if (total + percentage > 100 + 0.005) errors.percentage = `Quedan ${formatPercent(remaining)} para asignar`;
        if (errors.professional || errors.percentage) {
            setDraft(prev => ({ ...prev, errors }));
            return;
        }
        onChange([...assignments, {
            id: professional!.id,
            name: professional!.name,
            percentage,
            role: professional!.role,
            rolename: professional!.rolename,
        }]);
        setDraft(emptyDraft());
    };

    const columns: LoadTableColumn<Row>[] = [
        {
            key: 'professional',
            header: 'Profesional',
            className: 'nv-col-item',
            addCell: (
                <div className="nv-cell-field">
                    <Combobox
                        getItems={getProfessionals}
                        optionLabel="name"
                        placeholder="Buscá por nombre"
                        value={draft.text}
                        setValue={pickProfessional}
                        forceSelection={false}
                        error={Boolean(draft.errors.professional)}
                        caption={draft.errors.professional}
                    />
                </div>
            ),
            body: ({ assignment }) => <span className="nv-strong">{assignment.name}</span>,
        },
        {
            key: 'role',
            header: 'Rol',
            addCell: <span className="nv-muted">{draft.professional?.rolename || '—'}</span>,
            body: ({ assignment }) => <span className="nv-muted">{assignment.rolename || '—'}</span>,
        },
        {
            key: 'percentage',
            header: '%',
            align: 'right',
            className: 'nv-col-pct',
            addCell: (
                <div className="nv-cell-field">
                    <NumberField kind="percent" inCell ariaLabel="Porcentaje del profesional nuevo" value={draft.percentage} min={0} max={100}
                        invalid={Boolean(draft.errors.percentage)} describedBy="dist-add-pct-error"
                        onCommit={(percentage) => setDraft(prev => ({ ...prev, percentage, errors: { ...prev.errors, percentage: undefined } }))} />
                    <FieldError id="dist-add-pct-error" message={draft.errors.percentage} />
                </div>
            ),
            body: ({ assignment, index }) => (
                <NumberField kind="percent" inCell ariaLabel={`Porcentaje de ${assignment.name}`} value={assignment.percentage} min={0} max={100}
                    onCommit={(percentage) => setPercentage(index, percentage)} />
            ),
        },
        ...(hasMedicalAmount ? [{
            key: 'amount',
            header: 'Importe',
            align: 'right' as const,
            className: 'nv-col-money',
            addCell: <span className="nv-muted">{draft.percentage ? formatCurrency(amountOf(draft.percentage)) : '—'}</span>,
            body: ({ assignment, index }: Row) => (
                <NumberField kind="currency" inCell ariaLabel={`Importe de ${assignment.name}`} value={amountOf(assignment.percentage)} min={0} max={medicalAmount}
                    onCommit={(amount) => setPercentage(index, percentageOf(amount))} />
            ),
        }] : []),
    ];

    const complete = assignments.length > 0 && isFullyDistributed(total);
    const status = assignments.length === 0
        ? { className: 'nv-status-warn', icon: 'pi pi-exclamation-triangle', text: 'Asigná al menos un profesional' }
        : complete
            ? { className: 'nv-status-ok', icon: 'pi pi-check-circle', text: 'Distribución completa' }
            : total < 100
                ? { className: 'nv-status-warn', icon: 'pi pi-exclamation-triangle', text: `Faltan ${formatPercent(100 - total)} para completar` }
                : { className: 'nv-status-err', icon: 'pi pi-exclamation-triangle', text: `Sobran ${formatPercent(total - 100)}` };

    return (
        <section className="nv-section" aria-labelledby="nv-dist-title">
            <div className="nv-section-head">
                <div className="nv-stack-4">
                    <h3 className="nv-section-title" id="nv-dist-title">
                        Distribución de honorarios <span className="nv-title-detail">· {unitLabel}</span>
                    </h3>
                    {error && (
                        <small className="p-error" role="alert">
                            <i className="pi pi-exclamation-triangle" aria-hidden /> {error}
                        </small>
                    )}
                </div>
                <div className="nv-row-8">
                    {canApplyToAll && (
                        <Button
                            type="button"
                            className="buttonMain3 nv-tip"
                            label="Usar en todas las unidades"
                            data-pr-tooltip="Copia esta distribución en las demás unidades de la práctica"
                            onClick={onApplyToAll}
                        />
                    )}
                    <Button type="button" text className="nv-icon nv-tip" icon="pi pi-times" aria-label="Cerrar distribución" data-pr-tooltip="Cerrar distribución" onClick={onClose} />
                </div>
            </div>

            {/* TODO: estructura de distribución deshabilitada por ahora; se vuelve a habilitar más adelante.
                Iba acá, antes de la tabla: un Combobox "Estructura predefinida" (getDistributionStructures) que aplica el
                % de cada rol a los profesionales cargados, y el link "Crear estructura" que abre la creación
                (DistributionStructureDialog) dentro de este mismo panel, sin un modal encima. */}

            {readOnly ? (
                <DataTable value={rows} dataKey="rowKey" className="nv-load-table" emptyMessage="Sin profesionales asignados">
                    <Column header="Profesional" className="nv-col-item" body={({ assignment }: Row) => <span className="nv-strong">{assignment.name}</span>} />
                    <Column header="Rol" body={({ assignment }: Row) => <span className="nv-muted">{assignment.rolename || '—'}</span>} />
                    <Column header="%" align="right" alignHeader="right" className="nv-col-pct" body={({ assignment }: Row) => formatPercent(assignment.percentage)} />
                    {hasMedicalAmount && <Column header="Importe" align="right" alignHeader="right" className="nv-col-money" body={({ assignment }: Row) => formatCurrency(amountOf(assignment.percentage))} />}
                </DataTable>
            ) : (
                <LoadTable
                    rows={rows}
                    columns={columns}
                    addAction={<AddRowButton label="Agregar profesional" onClick={add} />}
                    rowActions={({ assignment, index }) => (
                        <Button
                            type="button"
                            text
                            className="nv-icon nv-tip"
                            icon="pi pi-trash"
                            aria-label={`Quitar a ${assignment.name}`}
                            data-pr-tooltip="Quitar"
                            onClick={() => onChange(assignments.filter((_, i) => i !== index))}
                        />
                    )}
                    emptyMessage="Sin profesionales asignados"
                    footerLabel="Total"
                    footerValues={{
                        percentage: formatPercent(total),
                        ...(hasMedicalAmount ? { amount: `${formatCurrency(amountOf(total))} de ${formatCurrency(medicalAmount)}` } : {}),
                    }}
                />
            )}

            <div className="nv-status-row">
                <span className={status.className}><i className={status.icon} aria-hidden /> {status.text}</span>
                {hasMedicalAmount && <span className="nv-muted">Honorario por unidad: {formatCurrency(medicalAmount)}</span>}
            </div>
        </section>
    );
}
